import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSupportRequestStreamConnected } from '../contexts/SupportRequestStreamContext';
import { invalidateRequestViews } from './supportRequestInvalidation';
import { retryUnlessClientError } from '../lib/queryRetry';
import { useDocumentVisible } from './useDocumentVisible';
import {
  supportRequestService,
  type CreateRequestPayload,
  type EllieArtifact,
  type RequestThread,
  type ListRequestsParams,
  type ListRequestsResult,
  type RequestAttachment,
  type RequestMessage,
  type RequestBadgeSummary,
  type RequestStatus,
  type RequestSubscriber,
  type RequestType,
  type SupportRequest,
} from '../services/supportRequest.service';

/**
 * Fallback poll interval, used only while the event stream is down.
 *
 * Real-time updates come from the SSE stream in `SupportRequestStreamContext`;
 * this timer exists for the cases where that connection can't be held — a proxy
 * that buffers SSE, a network that drops long-lived requests — and is tuned for
 * staying eventually-correct rather than feeling live. React Query already skips
 * `refetchInterval` in a background tab, so hidden tabs need nothing extra.
 */
const FALLBACK_POLL_MS = 60_000;

export function useSupportRequests(params?: ListRequestsParams) {
  return useQuery<ListRequestsResult>({
    queryKey: ['support-requests', params],
    queryFn: () => supportRequestService.list(params),
    staleTime: 15_000,
  });
}

export function useSupportRequest(id: string | null) {
  return useQuery<SupportRequest>({
    queryKey: ['support-request', id],
    queryFn: () => supportRequestService.get(id as string),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useRequestMessages(id: string | null) {
  const streaming = useSupportRequestStreamConnected();
  return useQuery<RequestMessage[]>({
    queryKey: ['support-request-messages', id],
    queryFn: () => supportRequestService.getMessages(id as string),
    enabled: !!id,
    staleTime: 5_000,
    refetchInterval: streaming ? false : FALLBACK_POLL_MS,
  });
}

export function useRequestAttachments(id: string | null) {
  return useQuery<RequestAttachment[]>({
    queryKey: ['support-request-attachments', id],
    queryFn: () => supportRequestService.getAttachments(id as string),
    enabled: !!id,
    staleTime: 15_000,
  });
}

/** Every artifact on the request, fetched once and shared by all the cards. */
export function useRequestArtifacts(id: string | null) {
  return useQuery<EllieArtifact[]>({
    queryKey: ['support-request-artifacts', id],
    queryFn: () => supportRequestService.getArtifacts(id as string),
    enabled: !!id,
    staleTime: 60_000,
  });
}

/**
 * The thread a message belongs to. Kept live like the main conversation, since
 * the takeover is a place people sit and watch for replies.
 */
export function useRequestThread(requestId: string | null, messageId: string | null) {
  const streaming = useSupportRequestStreamConnected();
  return useQuery<RequestThread>({
    queryKey: ['support-request-thread', requestId, messageId],
    queryFn: () => supportRequestService.getThread(requestId as string, messageId as string),
    enabled: !!requestId && !!messageId,
    staleTime: 5_000,
    refetchInterval: streaming ? false : FALLBACK_POLL_MS,
  });
}

export function useCreateSupportRequest() {
  const queryClient = useQueryClient();
  return useMutation<SupportRequest, Error, CreateRequestPayload>({
    mutationFn: (payload) => supportRequestService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-requests'] });
      queryClient.invalidateQueries({ queryKey: ['support-requests-unread'] });
    },
  });
}

export function usePostMessage(requestId: string | null) {
  const queryClient = useQueryClient();
  return useMutation<
    RequestMessage,
    Error,
    {
      body: string;
      attachmentIds?: string[];
      artifactIds?: string[];
      parentMessageId?: string | null;
    }
  >({
    mutationFn: ({ body, attachmentIds, artifactIds, parentMessageId }) =>
      supportRequestService.postMessage(requestId as string, body, {
        attachmentIds,
        artifactIds,
        parentMessageId,
      }),
    onSuccess: () => {
      // The message write also flips status and bumps counters on the request,
      // so the thread, the request itself and the list all go stale together.
      queryClient.invalidateQueries({ queryKey: ['support-request-messages', requestId] });
      queryClient.invalidateQueries({ queryKey: ['support-request', requestId] });
      queryClient.invalidateQueries({ queryKey: ['support-requests'] });
      queryClient.invalidateQueries({ queryKey: ['support-request-attachments', requestId] });
      queryClient.invalidateQueries({ queryKey: ['support-request-artifacts', requestId] });
      // A reply belongs to a thread whose query key we don't know here, so
      // invalidate every thread on this request rather than guessing the root.
      queryClient.invalidateQueries({ queryKey: ['support-request-thread', requestId] });
    },
  });
}

export function useRequestPermissions(id: string | null) {
  return useQuery<{ can_manage: boolean }>({
    queryKey: ['support-request-permissions', id],
    queryFn: () => supportRequestService.permissions(id as string),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateSupportRequest() {
  const queryClient = useQueryClient();
  return useMutation<
    SupportRequest,
    Error,
    { id: string; patch: { title?: string; description?: string; request_type?: RequestType } }
  >({
    mutationFn: ({ id, patch }) => supportRequestService.update(id, patch),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['support-request', id] });
      queryClient.invalidateQueries({ queryKey: ['support-requests'] });
    },
  });
}

export function useRequestSubscribers(id: string | null) {
  return useQuery<RequestSubscriber[]>({
    queryKey: ['support-request-subscribers', id],
    queryFn: () => supportRequestService.getSubscribers(id as string),
    enabled: !!id,
    staleTime: 30_000,
    retry: retryUnlessClientError,
  });
}

/**
 * Commit a checkbox selection by applying only what changed.
 *
 * Sends adds and removes rather than replacing the whole list, so two people
 * editing subscribers at once merge instead of overwriting each other — and so
 * a stale copy of the list in this tab can't silently unsubscribe someone added
 * from another. Both endpoints are idempotent, which is what makes a partial
 * failure safe to retry by pressing Save again.
 */
export function useUpdateRequestSubscribers() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    { id: string; add: string[]; remove: string[] }
  >({
    mutationFn: async ({ id, add, remove }) => {
      if (add.length) await supportRequestService.addSubscribers(id, add);
      for (const userId of remove) {
        await supportRequestService.removeSubscriber(id, userId);
      }
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['support-request-subscribers', id] });
      queryClient.invalidateQueries({ queryKey: ['support-request', id] });
    },
  });
}

export function useDeleteSupportRequest() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => supportRequestService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-requests'] });
      queryClient.invalidateQueries({ queryKey: ['support-requests-unread'] });
    },
  });
}

export function useEditMessage(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<RequestMessage, Error, { messageId: string; body: string }>({
    mutationFn: ({ messageId, body }) =>
      supportRequestService.editMessage(requestId, messageId, body),
    onSuccess: () => invalidateRequestViews(queryClient, requestId),
  });
}

export function useDeleteMessage(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (messageId) =>
      supportRequestService.deleteMessage(requestId, messageId),
    onSuccess: () => invalidateRequestViews(queryClient, requestId),
  });
}

export function useDetachFromMessage(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    RequestMessage,
    Error,
    { messageId: string; attachmentId?: string; artifactId?: string }
  >({
    mutationFn: ({ messageId, attachmentId, artifactId }) =>
      supportRequestService.detachFromMessage(requestId, messageId, {
        attachmentId,
        artifactId,
      }),
    onSuccess: () => invalidateRequestViews(queryClient, requestId),
  });
}

export function useSetRequestStatus() {
  const queryClient = useQueryClient();
  return useMutation<
    SupportRequest,
    Error,
    { id: string; status: RequestStatus; note?: string | null }
  >({
    mutationFn: ({ id, status, note }) =>
      supportRequestService.setStatus(id, status, note),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['support-request', id] });
      queryClient.invalidateQueries({ queryKey: ['support-request-messages', id] });
      queryClient.invalidateQueries({ queryKey: ['support-requests'] });
    },
  });
}

export function useUploadAttachment(requestId: string | null) {
  return useMutation<
    RequestAttachment,
    Error,
    { file: File; onProgress?: (pct: number) => void }
  >({
    mutationFn: ({ file, onProgress }) =>
      supportRequestService.uploadAttachment(requestId as string, file, onProgress),
  });
}

/** Drives the sidebar badge, since a reply can arrive on any page. */
export function useUnreadRequests(options?: { enabled?: boolean }) {
  const streaming = useSupportRequestStreamConnected();
  return useQuery<RequestBadgeSummary>({
    queryKey: ['support-requests-unread'],
    queryFn: () => supportRequestService.unreadSummary(),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
    refetchInterval: streaming ? false : FALLBACK_POLL_MS,
  });
}

/**
 * Move the read cursor when a thread is on screen.
 *
 * This is what stops the notification email: the debounced job re-checks the
 * cursor before sending, so opening the thread inside the debounce window means
 * no mail at all. Re-runs on message count so a reply that arrives while the
 * thread is open is also marked read rather than lingering as unread.
 */
export function useMarkRequestRead(
  requestId: string | null,
  messages: RequestMessage[],
  currentUserId: string | null | undefined,
) {
  const messageCount = messages.length;
  const queryClient = useQueryClient();
  const visible = useDocumentVisible();

  // The cursor every unread badge is measured against.
  //
  // `undefined` means "not known yet", deliberately distinct from `null`
  // ("never opened this request"): null badges everything, and rendering that
  // before the first response would flash badges onto an already-read thread.
  const [baseline, setBaseline] = useState<{
    requestId: string;
    at: string | null;
  } | null>(null);

  // Mirrors `baseline` so the effect can ask "have I already been watching this
  // request?" without depending on it and re-running every time it changes.
  const baselineRef = useRef<{ requestId: string; at: string | null } | null>(null);
  const apply = (next: { requestId: string; at: string | null }) => {
    baselineRef.current = next;
    setBaseline(next);
  };

  // The mark we have already issued, as `requestId:messageCount`.
  //
  // Marking read is NOT idempotent — it moves the cursor — so issuing it twice
  // for the same state destroys the very thing we need. React StrictMode runs
  // effects twice in development: both calls went out before either returned,
  // the first advanced the cursor to now, and the second then reported *that*
  // as `previous_read_at`. The baseline came back as "now", so nothing was ever
  // newer than it and no badge ever rendered.
  //
  // This also replaces the old `cancelled` flag. Cancellation was the wrong
  // question: the first call's answer was the correct one and got discarded
  // because its cleanup had already run. What actually matters is whether a
  // *newer* mark has superseded this one.
  const issuedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!requestId) return;

    // Only mark read while the tab is actually on screen. A backgrounded tab
    // left open on a request would otherwise keep advancing the cursor forever
    // — and the cursor is what suppresses notification email, so a tab parked
    // behind another window for three days would silence exactly the mail that
    // mattered. (The notifications module is built on the read cursor being an
    // honest "has seen it"; this is the half of that promise the client owes.)
    if (!visible) {
      // Coming back counts as arriving again, so whatever landed while away is
      // waiting to be found.
      baselineRef.current = null;
      issuedRef.current = null;
      return;
    }

    const key = `${requestId}:${messageCount}`;
    if (issuedRef.current === key) return;
    issuedRef.current = key;

    const arriving = baselineRef.current?.requestId !== requestId;

    supportRequestService
      .markRead(requestId)
      .then(({ previous_read_at, read_at }) => {
        // A newer mark has been issued; this answer is stale.
        if (issuedRef.current !== key) return;

        queryClient.invalidateQueries({ queryKey: ['support-requests-unread'] });

        // Only an ARRIVAL sets the baseline. A live mark must not touch it.
        //
        // `previous_read_at` on a live mark is the cursor our own arrival mark
        // wrote moments earlier — which is already *past* the message that just
        // rendered. Applying it moved the baseline forward and erased the badge
        // one render after it appeared, which is precisely what "the dot never
        // shows" looked like.
        //
        // The baseline may only move forward on the dwell below, when the
        // reader has actually had the message in front of them.
        if (arriving) {
          apply({ requestId, at: previous_read_at });
}
      })
      .catch(() => {
        // A failed read receipt is not worth surfacing — worst case the reader
        // gets an email about a thread they were looking at. Let it be retried
        // rather than leaving them with no baseline at all.
        if (issuedRef.current === key) issuedRef.current = null;
      });

  }, [requestId, messageCount, visible, queryClient]);

  // Replying clears the indicators.
  //
  // Posting is the unambiguous "I have dealt with this" signal — you cannot
  // answer a thread without having read it — so it retires every badge on the
  // request rather than only the ones above your reply.
  //
  // The baseline moves to the newest message's own timestamp rather than to
  // `Date.now()`: every other timestamp here is the server's, and mixing in the
  // browser's clock would make badges flicker on any machine whose clock runs
  // behind.
  useEffect(() => {
    const held = baselineRef.current;
    if (!requestId || !currentUserId || held?.requestId !== requestId) return;

    const since = held.at ? new Date(held.at).getTime() : -Infinity;
    let newest = since;
    let replied = false;
    for (const m of messages) {
      const at = new Date(m.created_at).getTime();
      if (at > newest) newest = at;
      if (m.author_id === currentUserId && at > since) replied = true;
    }
    if (!replied || newest === since) return;

    const at = new Date(newest).toISOString();
    apply({ requestId, at });
  }, [requestId, messages, currentUserId]);

  // Only answer for the request being asked about, so navigating between
  // requests doesn't briefly measure one thread against another's cursor.
  return baseline?.requestId === requestId ? baseline.at : undefined;
}
